<h1 style="text-align: center;">SymCrypt24 - Symmetric Cryptography and Boolean functions 2024</h1>

<h2>Organizers</h2>
<p>
    <b></b>
    <em>
        Marco Calderini (University of Trento, Italy)
        <br>
        George Petrides (University of Cyprus, Cyprus)
        <br>
        Irene Villa (University of Genoa & University of Trento, Italy)
    </em>
</p>
<h2>Description</h2>
<p>
    The workshop will be in two interrelated areas. The first one concerns different aspects of symmetric cryptography,
    such
    as the design of symmetric ciphers, attacks to these ciphers, as well as countermeasures. The second topic of
    interest
    is the theory of Boolean functions applied in cryptography scenarios, particularly in the design of symmetric
    ciphers.
    Such functions have found applications as S-boxes of block ciphers and feedback functions in stream ciphers, and the
    workshop will be focusing on their relevant cryptographic properties, their classification, and construction
    methods.
</p>
<h2>Program</h2>
<p>
    <i> September 27th, workshop session</i>
    <br>
    <i> Room C, 10:00 - 11:20</i>
</p>
<p>
    Invited speaker: <b>Sihem Mesnager</b>, University of Paris VIII (France)
</p>
<table class="orari" cellspacing="2" cellpadding="2" id="27afternoon">
    <tr bgcolor="lightblue" id="raipqc24">
        <th class="schedule" colspan="2">
            <font size="+1"><b>September 27th (morning) - Workshop Session</b></font>
        </th>
    </tr>
    <tr>
        <td class="schedule">
            10:00
            10:20
        </td>
        <td>
            <i>
                <b>Roberto La Scala</b>, University of Bari (Italy)
            </i>
            <br>
            Stream ciphers encoded by difference equations over finite fields and their cryptanalysis
            <details>
                <summary>
                    Abstract
                </summary>
                Many stream ciphers of practical interest, such as Trivium and Bluetooth's E0, can
                be modeled as systems of explicit ordinary difference equations with coefficients and
                solutions in a finite field. <br>
                These systems govern the evolution of the internal registers
                of these so-called "difference ciphers" over discrete time. By leveraging the formalism
                of difference equations, it is possible to define critical properties of stream ciphers,
                such as invertibility and periodicity, which are essential for assessing their security.
                This modeling, coupled with the corresponding cryptanalysis, actively supports the
                development of new ciphers.
            </details>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            10:20
            10:40
        </td>
        <td>
            <i>
                <b>Luca Mariot</b>, University of Twente (Netherlands)
            </i>
            <br>
            On the computation of the Walsh-Hadamard Transform using Binomial Trees
            (<a target="_blank" href="https://drive.google.com/file/d/19ebH25t96fbj-MQaiou5fLbm8H_-bmpK">slides</a>)
            <details>
                <summary>
                    Abstract
                </summary>
                In this talk, we investigate a new approach to compute the Walsh-
                Hadamard transform. The main data structure underlying this method
                is a binomial tree, where the path back to the root of each node represents a
                Walsh-Hadamard coefficient, and inductively defines the related scalar prod-
                uct. We show that the Walsh-Hadamard transform is obtained by visiting
                such a binomial tree, and consider both a depth-first and a breadth-first
                search strategy to perform this task. Although the resulting algorithms yield
                the same O(2^2n
                ) time complexity of the naive Walsh-Hadamard transform,
                we experimentally observe that they are faster in practice. We then show how
                to adapt the BFS-based algorithm to check a Boolean function’s correlation
                immunity order by requiring only its support as an input. Interestingly, this
                modified algorithm gives an efficient way to check whether a binary matrix
                of a large number of columns is an orthogonal array. Such a task cannot be
                practically accomplished with the Fast Walsh-Hadamard Transform proce-
                dure, due to the amount of memory required to hold the truth table of the
                associated Boolean function.
            </details>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            10:40
            11:15
        </td>
        <td>
            <i>
                <b>Sihem Mesnager</b>, University of Paris VIII (France)
            </i>
            <br>
            (Cryptographic) Functions for Designing Locally Recoverable Codes in Distributed Storage
            (<a target="_blank" href="https://drive.google.com/file/d/1qKjeqNopqm1lw8i0Wsoz55CxALNv_AR0">slides</a>)
            <details>
                <summary>
                    Abstract
                </summary>
                There is a crucial need for highly scalable and reliable extensive data storage systems due to
                the rapid growth in data. Distributed storage systems (DSSs) store data on multiple distributed
                nodes and are widely used in file system storage, ample database storage, backup files, cloud
                storage, and more. The repair problem in DSSs is a key focus area.
                Lately, there has been a notable increase in interest and focus on Locally Recoverable Codes
                (LRCs), highlighting their growing importance in distributed storage systems. In this talk, we
                will explore function-based codes and present mathematical techniques for creating (optimal)
                LRC codes from functions and polynomials. Notably, we emphasize the significant role of
                specific cryptographic functions in the design of LRCs.
            </details>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            11:15
            11:20
        </td>
        <td>
            <i>
                Closing remarks on SymCrypt24
            </i>
        </td>
    </tr>
</table>